import React, { useState } from 'react';
import styled, { css } from 'styled-components';
const RadioWrapper = styled.div `
  display: flex;
  gap: ${({ theme }) => theme.style.radioGap};
  font-size: ${({ theme }) => theme.style.radioFontSize};
  line-height: ${({ theme }) => theme.style.radioLineHeight};
  font-weight: ${({ theme }) => theme.style.radioFontWeight};
  white-space: nowrap;
  cursor: pointer;
  color: ${({ theme }) => theme.style.radioColor};
`;
const StyledRadio = styled.button `
  width: ${({ theme }) => theme.style.radioSize};
  height: ${({ theme }) => theme.style.radioSize};
  appearance: none;
  outline: none;
  border: none;
  background-color: ${({ theme }) => theme.style.radioBackgroundColor};
  padding: 0;
  margin: 0;
  user-select: none;
  border-radius: ${({ theme }) => theme.style.radioBorderRadius};
  cursor: pointer;
  transition:
    box-shadow 0.2s,
    background-color 0.2s;
  position: relative;
  box-shadow:
    0 0 0 var(--radio-outline, 0px) var(--radio-outline-color, transparent),
    var(--radio-border-properties) var(--radio-border-width)
      var(--radio-border-color, ${({ theme }) => theme.style.radioBorderColor}),
    var(--radio-shadow, ${({ theme }) => theme.style.radioShadow});

  background-color: ${({ $isHovered, theme }) => $isHovered && theme.style.radioHoveredBackgroundColor};

  --radio-border-properties: ${({ theme }) => theme.style.radioBorderProperties};
  --radio-border-width: ${({ theme }) => theme.style.radioBorderWidth};
  --radio-border-color: ${({ theme }) => theme.style.radioBorderColor};
  --radio-border-color: ${({ theme, $isHovered }) => $isHovered && theme.style.radioHoveredBorderColor};
  --radio-border-color: ${({ theme, $isFocussed }) => $isFocussed && theme.style.radioFocussedBorderColor};

  --radio-shadow: ${({ theme }) => theme.style.radioShadow};

  --radio-outline: ${({ theme, $isFocussed }) => $isFocussed && theme.style.radioFocussedOutline};
  --radio-outline-color: ${({ theme, $isFocussed }) => $isFocussed && theme.style.radioFocussedOutlineColor};

  cursor: ${({ $isDisabled }) => $isDisabled && 'not-allowed'};
  opacity: ${({ $isDisabled }) => $isDisabled && 0.5};

  ${({ $isChecked }) => $isChecked &&
    css `
      background-color: ${({ theme }) => theme.style.radioCheckedBackgroundColor};
      --radio-easing: ${({ theme }) => theme.style.radioEasing};
      --radio-duration: ${({ theme }) => theme.style.radioDuration};
      --radio-handle-opacity: ${({ theme }) => theme.style.radioCheckedHandleOpacity};
      --radio-handle-scale: ${({ theme }) => theme.style.radioCheckedHandleScale};

      --radio-border-properties: ${({ theme }) => theme.style.radioCheckedBorderProperties};
      --radio-border-color: ${({ theme }) => theme.style.radioCheckedBorderColor};
      --radio-border-width: ${({ theme }) => theme.style.radioCheckedBorderWidth};
      --radio-shadow: ${({ theme }) => theme.style.radioCheckedShadow};

      --radio-border-color: ${({ $isHovered, theme }) => $isHovered && theme.style.radioCheckedHoveredBorderColor};

      --radio-outline-color: ${({ theme, $isFocussed }) => $isFocussed && theme.style.radioCheckedFocussedBorderColor};
    `}

  &:after {
    content: '';
    display: block;
    left: ${({ theme }) => theme.style.radioHandleSpacing};
    top: ${({ theme }) => theme.style.radioHandleSpacing};
    border-radius: ${({ theme }) => theme.style.radioHandleBorderRadius};
    width: ${({ theme }) => theme.style.radioHandleSize};
    height: ${({ theme }) => theme.style.radioHandleSize};
    position: absolute;
    opacity: var(--radio-handle-opacity, ${({ theme }) => theme.style.radioHandleOpacity});
    box-shadow: ${({ theme }) => theme.style.radioHandleShadow};
    background: linear-gradient(
      ${({ theme }) => theme.style.radioHandleBackgroundColorTop},
      ${({ theme }) => theme.style.radioHandleBackgroundColorBottom}
    );
    transform: scale(var(--radio-handle-scale, ${({ theme }) => theme.style.radioHandleScale}));
    transition:
      background-color 0.2s,
      width var(--radio-duration, 0.2s) var(--radio-easing, ease),
      transform var(--radio-duration, 0.2s) var(--radio-easing, ease);
  }

  &:active {
    &:after {
      content: '';
      width: calc(${({ theme }) => theme.style.radioHandleSize} + 4px);
      transform: translateX(calc(var(--radio-x, 0) - 4px));
    }
  }
`;
export const Radio = (props) => {
    const [isHoveredState, setIsHoveredState] = useState(false);
    const [isFocussedState, setIsFocussedState] = useState(false);
    const [isPressedState, setIsPressedState] = useState(false);
    const styleProps = {
        $isChecked: props.isChecked ?? false,
        $isHovered: props.isHovered ?? isHoveredState,
        $isFocussed: props.isFocussed ?? isFocussedState,
        $isPressed: props.isPressed ?? isPressedState,
        $isDisabled: props.isDisabled ?? false,
    };
    const handleHoverChange = (value) => {
        if (props.onHoverChange && value !== isHoveredState) {
            props.onHoverChange(value);
        }
        setIsHoveredState(value);
    };
    const handleFocusChange = (value) => {
        if (props.isDisabled) {
            return;
        }
        if (props.onFocusChange && value !== isFocussedState) {
            props.onFocusChange(value);
        }
        setIsFocussedState(value);
    };
    const handlePressChange = (value) => {
        if (props.onPressChange && value !== isPressedState) {
            props.onPressChange(value);
        }
        if (value) {
            handleFocusChange(true);
        }
        setIsPressedState(value);
    };
    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (props.isDisabled) {
            return;
        }
        const isChecked = !(props.isChecked ?? false);
        if (props.onCheckedChange && props.isChecked !== isChecked) {
            props.onCheckedChange(isChecked);
        }
        handleFocusChange(false);
        handlePressChange(false);
    };
    return (React.createElement(RadioWrapper, { className: props.className, style: props.style, onClick: handleClick, onMouseEnter: () => handleHoverChange(true), onMouseMove: () => handleHoverChange(true), onMouseLeave: () => handleHoverChange(false), onFocus: () => handleFocusChange(true), onBlur: () => handleFocusChange(false), onMouseDown: (e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePressChange(true);
        }, onMouseUp: () => handlePressChange(false), ...styleProps },
        React.createElement(StyledRadio, { type: "button", onFocus: () => handleFocusChange(true), onBlur: () => handleFocusChange(false), ...styleProps }),
        props.label));
};
//# sourceMappingURL=Radio.js.map